import { Controller, useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import type { RegularOption } from "@/react/components/shared/uikit/Dropdown/Option";
import { useLiveStreamsConfig } from "@circle-react/components/LiveStreams/hooks";
import {
  ROOM_ACCESS_TYPES,
  ROOM_TYPES,
} from "@circle-react/helpers/liveStreamRoomHelpers";
import type { RoomType } from "@circle-react/helpers/liveStreamRoomHelpers";
import { Icon } from "@circle-react-shared/Icon";
import { DROPDOWN_SIZE, Dropdown } from "@circle-react-uikit/Dropdown";
import { Typography } from "@circle-react-uikit/Typography";

interface AccessTypeProps {
  name: string;
  roomType: RoomType;
}

interface AccessTypeOption {
  description: React.ReactNode;
  label: string;
  value: string;
}

export const AccessType = ({
  name,
  roomType = ROOM_TYPES.STREAM,
}: AccessTypeProps) => {
  const { config: liveStreamConfig } = useLiveStreamsConfig();
  const liveRoomCapacity = liveStreamConfig?.live_room_participants_capacity;
  const { control } = useFormContext();
  const accessTypeOptions: AccessTypeOption[] = [
    {
      value: ROOM_ACCESS_TYPES.OPEN,
      label: t("live_streams.form.access_type_community"),
      description: (
        <Typography.LabelSm>
          {t(`live_streams.form.${roomType}.access_type_community_label`, {
            maxCapacity: liveRoomCapacity,
          })}
        </Typography.LabelSm>
      ),
    },
    {
      value: ROOM_ACCESS_TYPES.SECRET,
      label: t("live_streams.form.access_type_secret"),
      description: (
        <Typography.LabelSm>
          {t(`live_streams.form.${roomType}.access_type_secret_label`)}
        </Typography.LabelSm>
      ),
    },
  ];

  if (roomType === ROOM_TYPES.STREAM) {
    accessTypeOptions.push({
      value: ROOM_ACCESS_TYPES.PUBLIC_STREAM,
      label: t("live_streams.form.access_type_public"),
      description: (
        <Typography.LabelSm>
          {t(`live_streams.form.${roomType}.access_type_public_label`)}
        </Typography.LabelSm>
      ),
    });
  }

  const getOption = (value: string) =>
    accessTypeOptions.find(option => option.value === value);

  return (
    <Controller<Record<string, string>>
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        const currentOption = getOption(value);

        return (
          <Dropdown
            direction="bottom-start"
            linkType="button"
            button={
              <Dropdown.MenuButton className="form-control relative inline-block w-full text-left">
                <div className="flex justify-between">
                  <div className="content-start">
                    <Typography.LabelMd>
                      {currentOption?.label}
                    </Typography.LabelMd>
                  </div>
                  <div className="flex content-end">
                    <Icon type="cheveron-down" />
                  </div>
                </div>
              </Dropdown.MenuButton>
            }
            buttonClassName="w-full"
            className="relative w-full"
            size={DROPDOWN_SIZE.EXTRA_LARGE}
            onChange={onChange}
            options={accessTypeOptions as RegularOption[]}
            labelWeight="semibold"
          />
        );
      }}
    />
  );
};
